/**
 * SEO Utilities for generating dynamic meta tags and social sharing content
 */

// Helper function to get optimized image URL with sizing
export const getOptimizedImageUrl = (imageUrl, width = 1200, height = 630) => {
  if (!imageUrl) {
    return null;
  }

  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http')) {
    return imageUrl;
  }

  // Add sizing parameters for optimization
  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}width=${width}&height=${height}`;
};

// Extract poster image from various content types
export const extractPosterImage = (content, contentType) => {
  let posterUrl = null;

  switch (contentType) {
    case 'movie':
      // Try movie poster, then channel poster, then thumbnail
      posterUrl =
        content.poster || content.channel?.poster || content.channel?.thumb || content.thumb;
      break;

    case 'series':
      // Try series poster, then channel poster, then thumbnail
      posterUrl =
        content.poster || content.channel?.poster || content.channel?.thumb || content.thumb;
      break;

    case 'channel':
      // Try channel poster, then thumbnail
      posterUrl = content.poster || content.thumb || content.channel_logo;
      break;

    case 'episode':
      // Try episode thumb, then series poster, then channel poster
      posterUrl =
        content.thumb ||
        content.series?.poster ||
        content.channel?.poster ||
        content.channel?.thumb;
      break;

    default:
      posterUrl = content.poster || content.thumb;
  }

  return getOptimizedImageUrl(posterUrl, 1200, 630);
};

// Generate SEO data for movies
export const generateMovieSEO = (movie, channelData = null) => {
  const title = `Watch ${movie.title || movie.name} | BKFC+`;
  const description =
    movie.description ||
    `Watch ${
      movie.title || movie.name
    } on BKFC+. Bare knuckle fighting live and on demand — the ultimate combat sports destination.`;

  const posterImage =
    extractPosterImage(movie, 'movie') || extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.movie',
    url: `/movie/${movie.slug || movie._id}`,
  };
};

// Generate SEO data for series
export const generateSeriesSEO = (series, channelData = null) => {
  const title = `${series.title || series.name} | BKFC+`;
  const description =
    series.description ||
    `Watch ${
      series.title || series.name
    } on BKFC+. Bare knuckle fighting live and on demand — the ultimate combat sports destination.`;

  const posterImage =
    extractPosterImage(series, 'series') || extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.tv_show',
    url: `/series/${series.slug || series._id}`,
  };
};

// Generate SEO data for episodes
export const generateEpisodeSEO = (episode, series, channelData = null) => {
  const episodeTitle = episode.title || `Episode ${episode.episode_number || ''}`;
  const seriesTitle = series?.title || series?.name || 'Series';

  const title = `${episodeTitle} - ${seriesTitle} | BKFC+`;
  const description =
    episode.description ||
    `Watch ${episodeTitle} from ${seriesTitle} on BKFC+. Bare knuckle fighting live and on demand.`;

  const posterImage =
    extractPosterImage(episode, 'episode') ||
    extractPosterImage(series, 'series') ||
    extractPosterImage(channelData, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'video.episode',
    url: `/watch/${channelData?.slug || channelData?._id}/${episode._id}`,
  };
};

// Generate SEO data for channels/live TV
export const generateChannelSEO = channel => {
  const title = `${channel.title || channel.name} - Live | BKFC+`;
  const description =
    channel.description ||
    `Watch ${
      channel.title || channel.name
    } live on BKFC+. The ultimate bare knuckle fighting and combat sports destination.`;

  const posterImage = extractPosterImage(channel, 'channel');

  return {
    title,
    description,
    image: posterImage,
    type: 'website',
    url: `/live-tv/${channel.slug || channel._id}`,
  };
};

// Generate SEO data for static pages
export const generatePageSEO = (pageType, customData = {}) => {
  const seoData = {
    homepage: {
      title: 'BKFC+',
      description:
        'BKFC+ — Watch bare knuckle fighting live and on demand. The ultimate combat sports destination.',
      url: '/',
    },
    about: {
      title: 'About BKFC+ - Bare Knuckle Fighting Championship',
      description:
        'Learn about BKFC+, the official streaming home of Bare Knuckle Fighting Championship. Watch live events and on-demand fights.',
      url: 'https://www.bkfc.com/about',
    },
    contact: {
      title: 'Contact BKFC+ - Support & Customer Service',
      description:
        'Get in touch with BKFC+ support team. Contact us for technical help, billing questions, or general inquiries about our streaming service.',
      url: 'https://www.bkfc.com/contact',
    },
    privacy: {
      title: 'Privacy Policy - BKFC+ Data Protection',
      description:
        'Read the BKFC+ privacy policy and learn how we protect your data and privacy rights across our streaming platform.',
      url: 'https://www.bkfc.com/privacy-policy',
    },
    terms: {
      title: 'Terms of Use - BKFC+ Legal Terms & Conditions',
      description:
        'Review the BKFC+ terms of use and legal conditions for using our streaming service. Understand your rights and responsibilities.',
      url: 'https://www.bkfc.com/terms-of-use',
    },
    'live-tv': {
      title: 'Live - BKFC+',
      description:
        'Watch bare knuckle fighting live on BKFC+. Catch live events and the latest action from the Bare Knuckle Fighting Championship.',
      url: '/live-tv',
    },
    movies: {
      title: 'Fights & Events | BKFC+',
      description:
        'Browse bare knuckle fights and events on BKFC+ — the ultimate combat sports destination, live and on demand.',
      url: '/movies',
    },
    series: {
      title: 'Series & Shows | BKFC+',
      description:
        'Watch BKFC+ original series and shows. Bare knuckle fighting and combat sports, live and on demand.',
      url: '/series',
    },
  };

  const defaultData = seoData[pageType] || seoData.homepage;

  return {
    ...defaultData,
    ...customData,
    type: 'website',
    image: customData.image || null, // Will use default BKFC image
  };
};

// --- Page titles (browser tab + analytics `page_title`) --------------------
// One route→title source of truth so every page has a correct, on-brand name —
// not a leftover or a stale inherited title. Resolved centrally
// in App.js on each route change; content pages (fighter/event/fight names) get
// a sensible category fallback here and upgrade to the specific title once their
// own meta component loads the content.
export const BRAND_NAME = 'BKFC+';
const titled = name => `${name} | ${BRAND_NAME}`;

// Exact paths (checked first, so /events/upcoming beats the /events/:x pattern).
const STATIC_PAGE_TITLES = {
  '/': BRAND_NAME,
  '/landing': BRAND_NAME,
  '/events': titled('Events'),
  '/events/upcoming': titled('Upcoming Events'),
  '/events/past': titled('Past Events'),
  '/fighters': titled('Fighters'),
  '/live-tv': titled('Watch Live'),
  '/movies': titled('Fights & Events'),
  '/series': titled('Shows & Series'),
  '/search': titled('Search'),
  '/my-lists': titled('My Favorites'),
  '/my-streams': titled('My Streams'),
  '/create-stream': titled('Create Stream'),
  '/settings': titled('Settings'),
  '/dashboard': titled('My Account'),
  '/login': titled('Log In'),
  '/signup': titled('Create Account'),
  '/create-account': titled('Create Account'),
  '/forgot-password': titled('Forgot Password'),
  '/reset-password': titled('Reset Password'),
  '/email-consent': titled('Email Preferences'),
  '/subscribe': titled('Choose Your Plan'),
};

// Dynamic content routes → category fallback (the page upgrades to the specific
// content name via its own DynamicMetaTags/MetaTags once the content loads).
const DYNAMIC_PAGE_TITLES = [
  [/^\/fighters\/[^/]+$/, titled('Fighters')],
  [/^\/movie\/[^/]+$/, titled('Fights & Events')],
  [/^\/series\/[^/]+$/, titled('Shows & Series')],
  [/^\/live-tv\/[^/]+$/, titled('Watch Live')],
  [/^\/category\/[^/]+$/, BRAND_NAME],
  [/^\/events\/[^/]+$/, titled('Events')],
  [/^\/watch\/[^/]+(?:\/[^/]+)?$/, BRAND_NAME],
  [/^\/my-lists\/[^/]+$/, titled('My Favorites')],
];

/**
 * Resolve the on-brand page title for a route path. Always returns a title
 * (falls back to the brand), so no page is ever left with a stale or default
 * name. Trailing slashes are ignored.
 * @param {string} pathname - location.pathname
 * @returns {string} the page title (e.g. "Fighters | BKFC+")
 */
export const resolvePageTitle = pathname => {
  const path = (pathname || '/').replace(/\/+$/, '') || '/';
  if (STATIC_PAGE_TITLES[path]) {
    return STATIC_PAGE_TITLES[path];
  }
  const match = DYNAMIC_PAGE_TITLES.find(([re]) => re.test(path));
  return match ? match[1] : BRAND_NAME;
};

// Generate structured data (JSON-LD) for search engines
export const generateStructuredData = (content, contentType) => {
  const baseData = {
    '@context': 'https://schema.org',
    publisher: {
      '@type': 'Organization',
      name: 'BKFC+',
      url: 'https://www.bkfc.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.bkfc.com/assets/images/bkfc-logo.png',
      },
    },
  };

  switch (contentType) {
    case 'movie':
      return {
        ...baseData,
        '@type': 'Movie',
        name: content.title || content.name,
        description: content.description,
        image: extractPosterImage(content, 'movie'),
        url: `https://www.bkfc.com/movie/${content.slug || content._id}`,
        datePublished: content.created_at,
        genre: content.genre || 'Combat Sports',
        duration: content.duration ? `PT${content.duration}M` : undefined,
      };

    case 'series':
      return {
        ...baseData,
        '@type': 'TVSeries',
        name: content.title || content.name,
        description: content.description,
        image: extractPosterImage(content, 'series'),
        url: `https://www.bkfc.com/series/${content.slug || content._id}`,
        datePublished: content.created_at,
        genre: content.genre || 'Combat Sports',
        numberOfEpisodes: content.playlist?.length,
      };

    case 'episode':
      return {
        ...baseData,
        '@type': 'TVEpisode',
        name: content.title,
        description: content.description,
        image: extractPosterImage(content, 'episode'),
        episodeNumber: content.episode_number,
        seasonNumber: content.season_number,
        partOfSeries: {
          '@type': 'TVSeries',
          name: content.series?.title || content.series?.name,
        },
      };

    default:
      return {
        ...baseData,
        '@type': 'WebSite',
        name: 'BKFC+',
        url: 'https://www.bkfc.com',
        description:
          'Bare knuckle fighting live and on demand — the ultimate combat sports destination',
      };
  }
};
